import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Check, EyeOff, ShieldCheck, X } from 'lucide-react';
import type { PrivacyInfoDto, ProductivityDayDto, ScreenshotListDto } from '@workforce/shared';
import { t } from '@/lib/i18n';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useTz } from '@/lib/timezone';
import { useCurrentPeriod } from '@/lib/periods';
import { Button, Card, ErrorBanner, Field, Input, Notice, PageHeader, Select, Spinner } from '@/components/ui';
import { Legend, ProductivityTable, ScreenshotGallery } from '@/components/ProductivityParts';
import { RANGE_OPTIONS, rangeFor, type RangePreset } from '@/components/AttendanceTable';
import { PersonDaysChart } from './ProductivityDashboard';

const WHEN: Record<PrivacyInfoDto['trackWhen'], string> = {
  clocked_in: 'Recorded only while you are clocked in',
  schedule: 'Recorded only during your scheduled working hours',
  always: 'Recorded whenever the computer is in use',
};

export function MyProductivityPage() {
  const { user } = useAuth();
  const { fmt } = useTz();
  const qc = useQueryClient();
  const zone = user?.employee?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const privacy = useQuery({ queryKey: ['me', 'productivity', 'privacy'], queryFn: () => api.get<PrivacyInfoDto>('/me/productivity/privacy') });
  const accept = useMutation({
    mutationFn: (v: string) => api.post<PrivacyInfoDto>('/me/productivity/consent', { noticeVersion: v }),
    onSuccess: (d) => qc.setQueryData(['me', 'productivity', 'privacy'], d),
  });
  const periods = useCurrentPeriod();
  const [preset, setPreset] = useState<RangePreset>('week');
  const [custom, setCustom] = useState(() => rangeFor('week', zone));
  const range = rangeFor(preset, zone, custom, periods.data);
  const days = useQuery({
    queryKey: ['me', 'productivity', 'days', range.from, range.to],
    queryFn: () => api.get<ProductivityDayDto[]>(`/me/productivity/days${qs(range)}`),
    enabled: !!user?.employee,
  });
  const screenshotsOn = !!privacy.data?.screenshots;
  const shots = useQuery({
    queryKey: ['me', 'productivity', 'shots', range.from, range.to],
    queryFn: () => api.get<ScreenshotListDto>(`/me/productivity/screenshots${qs(range)}`),
    enabled: !!user?.employee && screenshotsOn,
  });
  if (!user?.employee) return <Notice>{t('Your login is not linked to an employee record.')}</Notice>;
  if (privacy.isLoading) return <Spinner />;
  const p = privacy.data;
  const activeDevices = p?.devices.filter((d) => !d.revokedAt) ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title={t('My productivity')} subtitle={t('Exactly what your computer reports — the same figures your manager sees.')} />
      <ErrorBanner error={privacy.error ?? accept.error} />
      {p && (
        <Card title={<span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-600" /> {t('What is recorded')}</span>}>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3 text-sm">
              <p className="whitespace-pre-line rounded-lg bg-slate-50 p-3 text-slate-700">{p.noticeText}</p>
              {p.accepted ? (
                <p className="flex items-center gap-1.5 text-emerald-700"><Check className="h-4 w-4" /> {t('You accepted this notice on {date}.', { date: fmt(p.accepted.at, zone, 'dd LLL yyyy') })}</p>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <Button loading={accept.isPending} onClick={() => accept.mutate(p.noticeVersion)}>{t('I have read and accept')}</Button>
                  <span className="text-xs text-slate-500">{t('Nothing is recorded on your computer until you accept.')}</span>
                </div>
              )}
            </div>
            <ul className="space-y-2 text-sm">
              <Item ok>{t(WHEN[p.trackWhen])}</Item>
              {p.hoursSource === 'computer' && <Item ok>{t("Your working hours are taken from this activity (you don't clock in)")}</Item>}
              <Item ok>{t('The application in use (e.g. Revit, Excel)')}</Item>
              <Item ok={p.captureDomains}>{p.captureDomains ? t('Website names only (e.g. youtube.com) — not full addresses or searches') : t('Websites are not recorded')}</Item>
              <Item ok>{t('Idle time after {n} minutes without keyboard or mouse', { n: p.idleMinutes })}</Item>
              <Item ok={p.captureWindowTitles}>{p.captureWindowTitles ? t('Window titles are recorded') : t('Window titles are not recorded')}</Item>
              {p.screenshots ? (
                <Item ok>{t('Screenshots of the work screen, a few times an hour — you can see your own')}</Item>
              ) : (
                <Item ok={false} deny>{t('No screenshots')}</Item>
              )}
              <Item ok={false} deny>{t('No camera, no microphone')}</Item>
              <Item ok={false} deny>{t('No keystrokes or typed text')}</Item>
              <li className="pt-1 text-xs text-slate-500">{t('Detailed records are deleted after {n} days.', { n: p.retentionDays })}</li>
              {p.screenshots && <li className="text-xs text-slate-500">{t('Screenshots are deleted after {n} days.', { n: p.screenshotRetentionDays })}</li>}
            </ul>
          </div>
          {!p.enabled && <p className="mt-4 text-sm text-slate-500">{t('Tracking is currently turned off by the company.')}</p>}
          <p className="mt-4 text-xs text-slate-500">
            {t('Connected computers: {list}.', { list: activeDevices.length ? activeDevices.map((d) => (d.lastSeenAt ? t('{name} (last seen {time})', { name: d.hostname, time: fmt(d.lastSeenAt, zone, 'dd LLL HH:mm') }) : d.hostname)).join(t(', ')) : t('none yet') })}
          </p>
        </Card>
      )}
      {screenshotsOn && (
        <Card title={<span className="flex items-center gap-2"><Camera className="h-4 w-4 text-brand-600" /> {t('My screenshots')}</span>}>
          <p className="mb-3 text-sm text-slate-500">{t('These are the screenshots taken on your work computer — only you and the administrators can see them. If one caught something private, tell an administrator and they can remove it.')}</p>
          <ErrorBanner error={shots.error} />
          {shots.isLoading ? <Spinner /> : shots.data && (
            <ScreenshotGallery screenshots={shots.data.screenshots} imageBase="/api/me/productivity/screenshots" timezone={zone} autoThumbs />
          )}
        </Card>
      )}
      {days.data && <PersonDaysChart days={days.data} />}
      <Card
        title={t('My days')}
        bodyClass="p-0"
        actions={
          <Select value={preset} onChange={(e) => setPreset(e.target.value as RangePreset)} aria-label={t('Period')} className="w-36">
            {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
          </Select>
        }
      >
        {preset === 'custom' && (
          <div className="flex flex-wrap gap-3 border-b border-slate-100 px-5 py-3">
            <Field label={t('From')}><Input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} /></Field>
            <Field label={t('To')}><Input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} /></Field>
          </div>
        )}
        <div className="px-5 py-3"><Legend /></div>
        {days.isLoading ? <Spinner /> : days.data && <ProductivityTable days={days.data} />}
      </Card>
    </div>
  );
}

function Item({ ok, deny, children }: { ok: boolean; deny?: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      {ok ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : deny ? <X className="mt-0.5 h-4 w-4 shrink-0 text-red-500" /> : <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />}
      <span>{children}</span>
    </li>
  );
}
